import * as signalR from "@microsoft/signalr";
import { MutableRefObject } from "react";
import { API_URL } from "../_config/config.const";
import { accountService } from "../_services/accounts.service";

const RECONNECT_DELAYS = [0, 1000, 2000, 5000, 10000];
const REJOIN_INTERVAL_MS = 15_000;
const REJOIN_WAIT_MS = 8_000;
const REJOIN_POLL_MS = 150;

export type HubJoinInvoke = {
    joinMethod: string;
    leaveMethod: string;
    args: unknown[];
};

type ResilientHubOptions = {
    hubName: string;
    joinInvoke?: HubJoinInvoke;
    registerHandlers: (conn: signalR.HubConnection) => void;
    rejoinRef?: MutableRefObject<(() => Promise<void>) | null>;
    onConnectedChange?: (connected: boolean) => void;
};

function sleep(ms: number) {
    return new Promise<void>((resolve) => window.setTimeout(resolve, ms));
}

export function createResilientHubConnection({
    hubName,
    joinInvoke,
    registerHandlers,
    rejoinRef,
    onConnectedChange,
}: ResilientHubOptions) {
    let disposed = false;
    let startPromise: Promise<void> | null = null;

    const conn = new signalR.HubConnectionBuilder()
        .withUrl(`${API_URL}/hubs/${hubName}`, {
            accessTokenFactory: () => accountService.currentUserValue?.token ?? "",
        })
        .withAutomaticReconnect(RECONNECT_DELAYS)
        .build();

    conn.serverTimeoutInMilliseconds = 30_000;
    conn.keepAliveIntervalInMilliseconds = 10_000;

    registerHandlers(conn);

    const setConnected = (connected: boolean) => {
        onConnectedChange?.(connected);
    };

    const invokeJoin = async () => {
        if (!joinInvoke) return;
        await conn.invoke(joinInvoke.joinMethod, ...joinInvoke.args);
    };

    const waitUntilConnected = async (maxWaitMs: number) => {
        const deadline = Date.now() + maxWaitMs;

        while (Date.now() < deadline) {
            if (disposed) return false;
            if (conn.state === signalR.HubConnectionState.Connected) return true;
            if (conn.state === signalR.HubConnectionState.Disconnected) return false;
            await sleep(REJOIN_POLL_MS);
        }

        return conn.state === signalR.HubConnectionState.Connected;
    };

    const rejoinGroup = async (maxWaitMs = REJOIN_WAIT_MS) => {
        if (disposed || !joinInvoke) return;

        if (conn.state === signalR.HubConnectionState.Connected) {
            await invokeJoin();
            return;
        }

        if (conn.state === signalR.HubConnectionState.Reconnecting) {
            const ready = await waitUntilConnected(maxWaitMs);
            if (ready) await invokeJoin();
            return;
        }

        if (conn.state === signalR.HubConnectionState.Disconnected) {
            await startConnection();
        }
    };

    const startConnection = async () => {
        if (disposed) return;
        if (startPromise) return startPromise;

        if (conn.state === signalR.HubConnectionState.Reconnecting) {
            return rejoinGroup();
        }

        if (conn.state === signalR.HubConnectionState.Connected) {
            setConnected(true);
            return rejoinGroup(0);
        }

        startPromise = (async () => {
            try {
                if (conn.state === signalR.HubConnectionState.Disconnected) {
                    await conn.start();
                }
                if (conn.state === signalR.HubConnectionState.Connected) {
                    setConnected(true);
                    await invokeJoin();
                } else {
                    setConnected(false);
                }
            } catch (err) {
                console.error(`SignalR connect failed (${hubName})`, err);
                setConnected(false);
            } finally {
                startPromise = null;
            }
        })();

        return startPromise;
    };

    const scheduleRestart = () => {
        if (disposed) return;
        window.setTimeout(() => {
            if (disposed) return;
            if (conn.state === signalR.HubConnectionState.Disconnected) {
                startConnection();
            }
        }, 2000);
    };

    if (rejoinRef) {
        rejoinRef.current = () => rejoinGroup();
    }

    conn.onreconnecting(() => {
        setConnected(false);
    });

    conn.onreconnected(async () => {
        setConnected(true);
        try {
            await invokeJoin();
        } catch (err) {
            console.error(`SignalR rejoin failed (${hubName})`, err);
        }
    });

    conn.onclose(() => {
        setConnected(false);
        scheduleRestart();
    });

    const rejoinTimer = window.setInterval(() => {
        if (conn.state === signalR.HubConnectionState.Connected) {
            invokeJoin().catch(() => { });
        }
    }, REJOIN_INTERVAL_MS);

    const onBrowserOnline = () => {
        if (conn.state === signalR.HubConnectionState.Connected) {
            invokeJoin().catch(() => { });
            return;
        }
        if (conn.state === signalR.HubConnectionState.Reconnecting) {
            rejoinGroup().catch(() => { });
            return;
        }
        startConnection();
    };

    const onVisibilityChange = () => {
        if (document.visibilityState !== "visible") return;
        onBrowserOnline();
    };

    window.addEventListener("online", onBrowserOnline);
    document.addEventListener("visibilitychange", onVisibilityChange);

    startConnection();

    return () => {
        disposed = true;
        if (rejoinRef) rejoinRef.current = null;
        window.clearInterval(rejoinTimer);
        window.removeEventListener("online", onBrowserOnline);
        document.removeEventListener("visibilitychange", onVisibilityChange);

        (async () => {
            try {
                if (joinInvoke && conn.state === signalR.HubConnectionState.Connected) {
                    await conn.invoke(joinInvoke.leaveMethod, ...joinInvoke.args);
                }
            } catch {
            } finally {
                await conn.stop();
                setConnected(false);
            }
        })();
    };
}
